import {
  BILLING_PERIOD_LABELS,
  BILLING_PERIODS,
  ENTITY_STATUS,
  TARGET_AUDIENCE,
} from '@monorepo/shared/schemas/common'
import type { PlanResponse } from '@monorepo/shared/schemas/plans'
import { useNavigate } from '@tanstack/react-router'
import { Download, Plus, RefreshCw, Search } from 'lucide-react'
import { useState } from 'react'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { ViewToggle } from '@/shared/components/common/tabs/ViewToggle'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useAddonFilters, useAddons } from '../hooks/useAddons'
import type { PlanFilters } from '../types/addon.types'
import { AddonDrawer } from './AddonDrawer'
import { AddonsList } from './AddonsList'

export function AddonsPage() {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<PlanResponse | undefined>(
    undefined
  )

  const { filters, updateFilters, apiQuery } = useAddonFilters()
  const { data: plansResult, isLoading, error } = useAddons(apiQuery)

  const plans = plansResult?.data || []
  const meta = plansResult?.pagination

  const handleSearch = (search: string) => {
    updateFilters({ search, page: 1 })
  }

  const handleStatusFilter = (status: string) => {
    updateFilters({
      status: status as PlanFilters['status'],
      page: 1,
    })
  }

  const handleTargetAudienceFilter = (targetAudience: string) => {
    updateFilters({
      targetAudience: targetAudience as PlanFilters['targetAudience'],
      page: 1,
    })
  }

  const handleBillingPeriodChange = (value: string) => {
    updateFilters({
      billingPeriod: value as PlanFilters['billingPeriod'],
      page: 1,
    })
  }

  const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    updateFilters({ sortBy, sortOrder, page: 1 })
  }

  const handlePageChange = (page: number) => {
    updateFilters({ page })
  }

  const handlePlanClick = (plan: PlanResponse) => {
    navigate({ to: `/catalog/add-ons/${plan.id}` })
  }

  const handlePlanUpdate = (_updatedPlan: PlanResponse) => {
    // The useUpdatePlan hook will automatically invalidate queries and refresh the data
    // No additional action needed here as React Query handles the cache updates
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
        <div>
          <PageTitle
            title='Addons'
            subtitle='Create and manage addons for your agents and features'
          />
        </div>
        <div className='flex gap-3 items-center w-full md:w-auto'>
          <Button
            size='lg'
            className='flex-1 md:flex-initial'
            onClick={() => {
              setSelectedPlan(undefined)
              setDrawerOpen(true)
            }}
          >
            <Plus className='w-5 h-5 mr-2' />
            Create Addon
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <div className='flex gap-2 items-center flex-1 max-w-4xl'>
          <div className='relative flex-1 min-w-[300px]'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
            <Input
              placeholder='Search addons...'
              value={filters.search || ''}
              onChange={e => handleSearch(e.target.value)}
              className='pl-10'
            />
          </div>

          <Select
            value={filters.status || 'all'}
            onValueChange={handleStatusFilter}
          >
            <SelectTrigger className='w-40'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All</SelectItem>
              <SelectItem value={ENTITY_STATUS.active}>Active</SelectItem>
              <SelectItem value={ENTITY_STATUS.draft}>Draft</SelectItem>
              <SelectItem value={ENTITY_STATUS.archived}>Archived</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.targetAudience || 'all'}
            onValueChange={handleTargetAudienceFilter}
          >
            <SelectTrigger className='w-40'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Audiences</SelectItem>
              <SelectItem value={TARGET_AUDIENCE.human}>For Humans</SelectItem>
              <SelectItem value={TARGET_AUDIENCE.agent}>For Agents</SelectItem>
              <SelectItem value={TARGET_AUDIENCE.both}>For Both</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.billingPeriod || 'all'}
            onValueChange={handleBillingPeriodChange}
          >
            <SelectTrigger className='w-44'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Periods</SelectItem>
              {BILLING_PERIODS.map(period => (
                <SelectItem key={period} value={period}>
                  {BILLING_PERIOD_LABELS[period]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='flex gap-2 items-center'>
          <Button variant='outline' size='sm'>
            <RefreshCw className='w-4 h-4' />
          </Button>

          <Button variant='outline' size='sm'>
            <Download className='w-4 h-4' />
          </Button>

          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {/* Plans List */}
      <AddonsList
        plans={plans}
        isLoading={isLoading}
        error={error}
        viewMode={viewMode}
        filters={filters}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onPlanClick={handlePlanClick}
        onPlanUpdate={handlePlanUpdate}
        meta={meta}
      />

      {/* Plan Drawer - Unified Create/Edit */}
      <AddonDrawer
        plan={selectedPlan}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  )
}
